import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Layout from '../../../../layouts';
import Page from '../../../../components/Page';
import useAuth from '../../../../hooks/useAuth';
import axiosInstance from '../../../../utils/axios';
import { isMaster } from '../../../../utils/perfil';
import { BotaoExportar, Num, CABECALHO_TABELA } from '../../../../components/gps';
import Modal, { BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from '../../../../components/gps/Modal';
import { ResumoExclusao } from '../../../../components/gps/MenuAcoes';
import TituloTela from '../../../../components/gps/TituloTela';

/**
 * Categorias de ticket e prazos de SLA (em dias uteis) por prioridade.
 *
 * Somente MASTER - guard dentro da propria pagina, nao apenas no menu.
 *
 * Migrada para o visual novo (shadcn + Tailwind) SEM mudanca de comportamento:
 * mesmos endpoints, mesma validacao de 1 a 365 dias, mesmo salvamento no blur.
 *
 * O prazo continua editavel celula por celula: a mesma categoria costuma ter
 * prazos diferentes por prioridade (URGENTE 1 dia, BAIXA 5 dias), e obrigar a
 * abrir um formulario por combinacao seria 4x mais cliques.
 *
 * UMA MELHORA sobre a versao MUI: cada prioridade tem tom proprio no cabecalho,
 * de vermelho (urgente) a neutro (baixa). Com quatro colunas de numeros iguais
 * lado a lado, sem cor o olho perde a coluna no meio da linha - e um prazo
 * digitado na coluna errada so aparece semanas depois, como SLA estourado.
 */

type Categoria = {
  codigo: number;
  nome: string;
  descricao: string | null;
  status: number;
  /** Prioridade com que a categoria e atendida - define a de todo ticket dela. */
  prioridade?: string;
  prazos: Array<{ prioridade: string; prazoDiasUteis: number }>;
};

const PRIORIDADES = ['URGENTE', 'ALTA', 'MEDIA', 'BAIXA'];

const TOM_PRIORIDADE: Record<string, string> = {
  URGENTE: 'text-danger',
  ALTA: 'text-info',
  MEDIA: 'text-muted-foreground',
  BAIXA: 'text-faint',
};

const GRADE = 'minmax(220px,2.4fr) repeat(4, 96px) 90px';

CategoriasPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function CategoriasPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const ehMaster = isMaster(user);

  const [itens, setItens] = useState<Categoria[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [salvandoPrazo, setSalvandoPrazo] = useState<string | null>(null);

  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState<Categoria | null>(null);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [prioridade, setPrioridade] = useState('MEDIA');

  /* `silencioso`: recarrega SEM ligar `carregando`. Salvar um prazo chamava a
     carga normal, as linhas viravam "Carregando..." e eram remontadas - o foco
     e os digitos ja escritos na celula seguinte (Tab de URGENTE para ALTA)
     sumiam sem salvar. */
  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    setErro('');
    try {
      const res = await axiosInstance.get<Categoria[]>('tickets/categorias/listar');
      setItens(res.data ?? []);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar as categorias.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (ehMaster) carregar();
  }, [carregar, ehMaster]);

  if (!ehMaster) {
    return (
      <Page title="Categorias e SLA">
        <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
          <div className="rounded-lg border border-line bg-danger-soft p-4 text-[12.5px] text-danger">
            Apenas o perfil <strong>Master</strong> pode configurar categorias e prazos de SLA.
          </div>
        </div>
      </Page>
    );
  }

  const prazoDe = (categoria: Categoria, prioridade: string) =>
    categoria.prazos?.find((p) => p.prioridade === prioridade)?.prazoDiasUteis ?? '';

  /* DEVOLVE SE GRAVOU (28/09/2026).

     O input e NAO CONTROLADO (`defaultValue`) e a `key` nao muda, entao o React
     nunca reescreve o valor no DOM depois da montagem. Quando o prazo era
     recusado - fora de 1 a 365 - ou o POST falhava, saia um aviso passageiro e
     a celula CONTINUAVA exibindo o numero digitado, como se fosse o SLA
     vigente. O servidor tinha outro valor, e so um F5 mostrava a verdade.

     Numa tela de SLA isso decide prioridade de atendimento: o Master digita
     400, ve o aviso sumir e segue achando que gravou.

     O `onBlur` usa este retorno para devolver a celula ao valor real. */
  const salvarPrazo = async (categoria: Categoria, prioridade: string, valor: string): Promise<boolean> => {
    const dias = Number(valor);
    if (!Number.isInteger(dias) || dias < 1 || dias > 365) {
      enqueueSnackbar('Prazo deve ser um número de dias úteis entre 1 e 365.', { variant: 'warning' });
      return false;
    }

    const chave = `${categoria.codigo}-${prioridade}`;
    setSalvandoPrazo(chave);
    try {
      await axiosInstance.post('tickets/sla', {
        idCategoria: categoria.codigo,
        prioridade,
        prazoDiasUteis: dias,
      });
      enqueueSnackbar(`Prazo de ${prioridade} atualizado.`, { variant: 'success' });
      carregar(true);
      return true;
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao salvar o prazo.', { variant: 'error' });
      return false;
    } finally {
      setSalvandoPrazo(null);
    }
  };

  const salvarCategoria = async () => {
    if (!nome.trim()) {
      enqueueSnackbar('Informe o nome da categoria.', { variant: 'warning' });
      return;
    }
    setSalvando(true);
    try {
      const corpo = { nome: nome.trim(), descricao: descricao.trim() || null, prioridade };
      if (editando) await axiosInstance.put(`tickets/categorias/${editando.codigo}`, corpo);
      else await axiosInstance.post('tickets/categorias', corpo);

      enqueueSnackbar('Categoria salva.', { variant: 'success' });
      setAberto(false);
      carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao salvar a categoria.', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  /* Confirmacao antes de apagar (30/09/2026). A lixeira fica ao lado do lapis e
     apagava num clique - um clique errado tirava uma categoria de SLA. As outras telas (APK, usuario, grupo, politica)
     ja confirmavam. */
  const [apagando, setApagando] = useState<Categoria | null>(null);
  const [removendo, setRemovendo] = useState(false);

  const remover = async (categoria: Categoria) => {
    setRemovendo(true);
    try {
      await axiosInstance.delete(`tickets/categorias/${categoria.codigo}`);
      setApagando(null);
      enqueueSnackbar('Categoria removida.', { variant: 'success' });
      carregar();
    } catch (e: any) {
      // O backend recusa remover categoria com ticket em aberto e explica o
      // motivo na mensagem - repassamos ao usuario em vez de um erro genérico.
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao remover.', { variant: 'error' });
    } finally {
      setRemovendo(false);
    }
  };

  return (
    <Page title="Categorias e SLA">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <TituloTela
              descricao={
                <>
O prazo é contado em <strong>dias úteis</strong>, descontando sábados, domingos e os feriados
              cadastrados. Alterar um prazo aqui vale para os <strong>próximos</strong> tickets — os já
              abertos mantêm o prazo calculado na abertura.
                </>
              }
            >
              Categorias e SLA
            </TituloTela>
          </div>

          <div className="flex flex-none items-center gap-2">
            {/* Uma coluna por prioridade: o prazo e o dado que se consulta nesta
                tela e mora numa lista aninhada; achatar aqui evita planilha com
                JSON dentro da celula. */}
            <BotaoExportar
              nomeArquivo="categorias-sla"
              linhas={() =>
                itens.map((c) => {
                  const registro: Record<string, any> = {
                    Codigo: c.codigo,
                    Nome: c.nome ?? '',
                    Descricao: c.descricao ?? '',
                    Status: Number(c.status) === 1 ? 'ATIVO' : 'INATIVO',
                  };
                  PRIORIDADES.forEach((pr) => {
                    const achado = (c.prazos || []).find((x) => x.prioridade === pr);
                    registro['Prazo ' + pr + ' (dias uteis)'] = achado ? achado.prazoDiasUteis : '';
                  });
                  return registro;
                })
              }
            />
            <button
              type="button"
              onClick={() => {
                setEditando(null);
                setNome('');
                setDescricao('');
                setPrioridade('MEDIA');
                setAberto(true);
              }}
              className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
            >
              + Nova categoria
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div
                className={CABECALHO_TABELA}
                style={{ gridTemplateColumns: GRADE }}
              >
                <div className="text-faint">Categoria</div>
                {PRIORIDADES.map((p) => (
                  <div key={p} className={`text-center ${TOM_PRIORIDADE[p]}`}>
                    {p}
                  </div>
                ))}
                <div className="text-right text-faint">Ações</div>
              </div>

              {carregando && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">Carregando…</div>
              )}

              {!carregando && erro && <div className="p-8 text-center text-[12.5px] text-danger">{erro}</div>}

              {!carregando && !erro && itens.length === 0 && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                  Nenhuma categoria cadastrada.
                </div>
              )}

              {!carregando &&
                !erro &&
                itens.map((categoria) => (
                  <div
                    key={categoria.codigo}
                    className="grid items-center border-b px-3.5 py-2 hover:bg-surface-2"
                    style={{ gridTemplateColumns: GRADE, borderColor: 'var(--gps-soft)' }}
                  >
                    <div className="min-w-0 pr-3">
                      <span className="block truncate text-[12.5px] font-semibold">{categoria.nome}</span>
                      {categoria.descricao && (
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {categoria.descricao}
                        </span>
                      )}
                    </div>

                    {PRIORIDADES.map((prioridade) => (
                      <div key={prioridade} className="flex justify-center">
                        <input
                          type="number"
                          min={1}
                          max={365}
                          defaultValue={prazoDe(categoria, prioridade)}
                          disabled={salvandoPrazo === `${categoria.codigo}-${prioridade}`}
                          // Salva ao SAIR do campo, nao a cada tecla: evita uma
                          // requisicao por digito.
                          onBlur={async (e) => {
                            const atual = String(prazoDe(categoria, prioridade));
                            if (e.target.value === atual) return;
                            const campo = e.target;
                            const gravou = await salvarPrazo(categoria, prioridade, campo.value);
                            /* Nao gravou, a celula volta ao valor real. Campo nao
                               controlado nao se corrige sozinho - ver salvarPrazo. */
                            if (!gravou) campo.value = atual;
                          }}
                          className="h-8 w-[64px] rounded-lg border border-line bg-surface text-center text-[12.5px] tabular-nums text-ink outline-none focus:border-primary disabled:opacity-40"
                          aria-label={`Prazo ${prioridade} de ${categoria.nome}`}
                        />
                      </div>
                    ))}

                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="Editar categoria"
                        aria-label="Editar categoria"
                        onClick={() => {
                          setEditando(categoria);
                          setNome(categoria.nome);
                          setDescricao(categoria.descricao ?? '');
                          setPrioridade(categoria.prioridade || 'MEDIA');
                          setAberto(true);
                        }}
                        className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-soft hover:text-ink"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        title="Remover categoria"
                        aria-label="Remover categoria"
                        onClick={() => setApagando(categoria)}
                        className="grid h-8 w-8 place-items-center rounded-md text-danger hover:bg-danger-soft"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="border-t border-line px-3.5 py-2.5 text-[12px] text-muted-foreground">
            <Num>{itens.length}</Num> categorias · prazo em dias úteis
          </div>
        </div>
      </div>

      <Modal
        aberto={aberto}
        titulo={editando ? 'Editar categoria' : 'Nova categoria'}
        descricao={editando ? subtituloCategoria(editando) : 'Define como o chamado é classificado na abertura.'}
        icone={editando ? '✎' : '+'}
        onFechar={() => setAberto(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setAberto(false)} disabled={salvando}>
              Cancelar
            </BotaoSecundario>
            <BotaoPrimario onClick={salvarCategoria} disabled={salvando || !nome.trim()}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </BotaoPrimario>
          </>
        }
      >
        <Campo rotulo="Nome" obrigatorio>
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            maxLength={120}
            autoFocus
            className={classesEntrada}
          />
        </Campo>

        <Campo rotulo="Descrição" dica="Aparece para quem abre o chamado — use para separar casos parecidos.">
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            maxLength={500}
            rows={3}
            className="w-full rounded-lg border border-line bg-surface p-3 text-[12.5px] text-ink outline-none placeholder:text-faint focus:border-primary"
          />
        </Campo>

        {/* PRIORIDADE DA CATEGORIA (29/09/2026).

            Passou a ser aqui porque saiu da tela de abertura: quem abre o
            chamado descreve o problema, nao negocia o prazo. Esta escolha define
            com que urgencia TODO chamado da categoria entra na fila, e e a unica
            forma de isso mudar - por isso a alteracao e auditada no servidor. */}
        <Campo
          bloco
          rotulo="Prioridade de atendimento"
          obrigatorio
          dicaVisivel
          dica="Vale para todo chamado desta categoria. Junto com ela, define o prazo na tabela abaixo."
        >
          <div className="inline-flex h-9 items-center gap-1 rounded-lg border border-line bg-surface-2 p-1">
            {PRIORIDADES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPrioridade(p)}
                className={`h-7 rounded-md px-2.5 text-[11px] font-bold ${
                  prioridade === p ? `bg-surface shadow-card ${TOM_PRIORIDADE[p] || 'text-ink'}` : 'text-muted-foreground'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </Campo>

        {!editando && (
          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
            A categoria nova já nasce com prazos padrão (urgente 1, alta 2, média 3, baixa 5 dias úteis).
            Você ajusta na tabela depois.
          </div>
        )}
      </Modal>
      <Modal
        aberto={Boolean(apagando)}
        titulo="Remover categoria?"
        icone="!"
        largura="max-w-[460px]"
        onFechar={() => !removendo && setApagando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setApagando(null)} disabled={removendo}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => apagando && remover(apagando)}
              disabled={removendo}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {removendo ? 'Removendo...' : 'Remover'}
            </button>
          </>
        }
      >
        {apagando && <ResumoExclusao titulo={apagando.nome} extra={<>Os prazos de SLA desta categoria deixam de valer para os próximos tickets.</>} />}
      </Modal>
    </Page>
  );
}

function subtituloCategoria(categoria: Categoria) {
  const total = categoria.prazos?.length ?? 0;
  return `${categoria.nome} · ${total} prazo(s) configurado(s)`;
}
